import { useQuery } from '@apollo/react-hooks';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSessionData } from '@confluence/session-data';
import { useSitePersonalizationQuery } from '@confluence/onboarding-helpers/entry-points/hooks/useSitePersonalizationQuery';
import { isErrorMarkedAsHandled, markErrorAsHandled, getAGGClient } from '@confluence/graphql';

import { TrialEndTimestampQuery } from '../graphql/TrialEndTimestampQuery.agggraphql';

type SitePersonalizationAttributes = {
	name: string;
	value: string;
};

export const useDirectToPremiumTrial = (skip: boolean) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const PRODUCT_KEY = 'confluence.ondemand';
	const STANDARD_OFFERING_KEY = '2eda1119-fab4-4699-ab37-ff8f12052cde';
	const { cloudId } = useSessionData();

	// get trial start timestamp from personalization API
	const {
		error: personalizationError,
		loading: personalizationLoading,
		attributes,
	} = useSitePersonalizationQuery({ cloudId, skip });

	// get trial end timestamp from tenant context
	const {
		data,
		loading: trialEndTimestampLoading,
		error: trialEndTimestampError,
	} = useQuery(TrialEndTimestampQuery, {
		client: getAGGClient(),
		variables: {
			cloudId,
			productKey: PRODUCT_KEY,
			offeringKey: STANDARD_OFFERING_KEY,
		},
		skip,
	});

	const trialEndTimeMs =
		data?.tenantContexts?.[0]?.entitlementInfo?.entitlement?.subscription?.trial?.endTimestamp;

	const error = personalizationError || trialEndTimestampError;
	const loading = personalizationLoading || trialEndTimestampLoading;

	if (error && !isErrorMarkedAsHandled(error)) {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'failed',
				actionSubject: 'useDirectToPremiumTrial',
				source: 'RecommendStandardOverPremiumModal',
				attributes: {
					growthFeature: 'RecommendStandardOverPremiumModal',
					personalizationError: personalizationError?.message,
					trialEndTimestampError: trialEndTimestampError?.message,
				},
			},
		}).fire();
		markErrorAsHandled(error);
	}

	const isDirectToPremiumTrial = isConfluenceDirectToTrial(attributes, trialEndTimeMs);
	return { error, loading, isDirectToPremiumTrial };
};

const isConfluenceDirectToTrial = (
	traits: SitePersonalizationAttributes[],
	trialEndTimeMs: number,
): boolean => {
	// 1000 milliseconds per second, 60 seconds per minute
	const oneMinuteInMs = 60 * 1000;
	// 60 minutes per hour, 24 hours per day
	const oneDayInMs = oneMinuteInMs * 60 * 24;

	const confluenceActivationDateTrait = traits.find(
		(trait) => trait.name === 'confluence.ondemand.activationDate',
	);

	if (!confluenceActivationDateTrait?.value) {
		return false;
	}

	if (typeof confluenceActivationDateTrait.value !== 'string') {
		return false;
	}

	const confluenceActivationDate = new Date(confluenceActivationDateTrait.value);
	const trialStartDate = new Date(trialEndTimeMs - 30 * oneDayInMs);
	// give 5 minute buffer in case of delay in system
	return trialStartDate.getTime() - confluenceActivationDate.getTime() < oneMinuteInMs * 5;
};
